import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import {
  getCompanyActionPlansFocusArea,
  getCompanyActionPlansOverall,
  getCompanyActionPlansStats,
} from 'apis/ActionPlansAPI';
import PillSwitcher from 'components/lib/PillSwitcher';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleSetSelectedCommunity, handleSetSelectedParentCommunity } from 'store/slices/globalSlice';
import { ActionPlansCompanyFiltersState } from 'types/actionPlans';
import { PaginatedResponseType, SelectAdvancedFilterOptionType } from 'types/common';
import useDataList from 'utils/hooks/useDataList';

import ActionPlansCompanyFilters from './ActionPlansCompanyFilters';
import ActionPlansCompanyFocusAreaTable from './ActionPlansCompanyFocusAreaTable';
import ActionPlansCompanyHeader from './ActionPlansCompanyHeader';
import ActionPlansCompanyOverallTable from './ActionPlansCompanyOverallTable';
import ActionPlansCompanySummary from './ActionPlansCompanySummary';
import { ACTION_PLANS_COMPANY_STATUS, EMPTY_FILTERS, OVERALL_VIEW_KEY } from './constants';
import useActionPlanFocusAreas from '../useActionPlanFocusAreas';

const Container = styled.div`
  width: 100%;
`;

const PageContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1360px;
  margin: 10px auto 30px;
  padding: 0 30px 30px;
`;

const PillsSection = styled.div`
  margin-bottom: 20px;
`;

const ActionPlansTabs = styled(PillSwitcher)`
  gap: 5px;
  flex-wrap: nowrap;
  overflow-x: auto;

  button {
    flex: 0 0 auto;
    padding-right: 9px;
    padding-left: 9px;
  }
`;

const SummarySection = styled.div`
  margin-bottom: 20px;
`;

const FiltersSection = styled.div`
  margin-bottom: 10px;
`;

const ActionPlansCompanyView = () => {
  const dispatch = useAppDispatch();
  const { selectedCompany, contextCompany, companies, communities, selectedCommunity } = useAppSelector(
    (state) => state.global
  );

  const company = contextCompany ?? selectedCompany ?? companies?.[0] ?? null;
  const resolvedCompanyId = company?.id;

  useEffect(() => {
    if (selectedCommunity) {
      dispatch(handleSetSelectedCommunity(undefined));
      dispatch(handleSetSelectedParentCommunity(undefined));
    }
  }, [dispatch, selectedCommunity]);

  const [activeTab, setActiveTab] = useState<string | number>(OVERALL_VIEW_KEY);
  const isOverall = activeTab === OVERALL_VIEW_KEY;
  const activeFocusAreaId = typeof activeTab === 'number' ? activeTab : Number(activeTab);

  const { focusAreaOptions } = useActionPlanFocusAreas();
  const activeFocusAreaLabel = isOverall
    ? undefined
    : focusAreaOptions.find((option) => option.value === activeFocusAreaId)?.label;
  const tabs = useMemo(
    () => [
      { key: OVERALL_VIEW_KEY, label: 'Overall' },
      ...focusAreaOptions.map((option) => ({ key: option.value, label: option.label })),
    ],
    [focusAreaOptions]
  );

  const { items, totalItemsCount, filters, ordering, setItems, setPage, setFilters, setOrdering } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: PAGINATION_PAGE_SIZE.SMALL,
        ...EMPTY_FILTERS,
      },
    },
  });

  const tableFilters: ActionPlansCompanyFiltersState = {
    statuses: filters.statuses,
    communityIds: filters.communityIds,
    planStatuses: filters.planStatuses,
    search: filters.search,
  };

  const handleTabChange = (key: string | number) => {
    if (key === activeTab) {
      return;
    }
    setActiveTab(key);
    setFilters({ ...EMPTY_FILTERS });
    setItems({ results: [], count: 0 });
  };

  const handleFiltersChange = (next: Partial<ActionPlansCompanyFiltersState>) => setFilters(next);

  const handleOrderingChange = (nextOrdering: string | undefined) => setOrdering(nextOrdering as string);

  const overdueActive = isOverall
    ? tableFilters.statuses.includes(ACTION_PLANS_COMPANY_STATUS.OVERDUE)
    : tableFilters.planStatuses.includes(ACTION_PLANS_COMPANY_STATUS.OVERDUE);

  const handleToggleOverdue = () => {
    if (isOverall) {
      handleFiltersChange({ statuses: overdueActive ? [] : [ACTION_PLANS_COMPANY_STATUS.OVERDUE] });
    } else {
      handleFiltersChange({
        planStatuses: overdueActive ? [] : [ACTION_PLANS_COMPANY_STATUS.OVERDUE],
      });
    }
  };

  const { data: stats } = useQuery({
    queryKey: ['action-plans-company-stats', resolvedCompanyId, activeTab],
    queryFn: () => getCompanyActionPlansStats(resolvedCompanyId as number, isOverall ? undefined : activeFocusAreaId),
    enabled: Boolean(resolvedCompanyId),
    refetchOnWindowFocus: false,
  });

  useQuery<PaginatedResponseType<any>>({
    queryKey: ['action-plans-company', resolvedCompanyId, activeTab, filters, ordering],
    queryFn: () =>
      isOverall
        ? getCompanyActionPlansOverall(resolvedCompanyId as number, {
            page: filters.page,
            page_size: filters.pageSize,
            status: tableFilters.statuses,
            search: filters.search,
            ordering,
          })
        : getCompanyActionPlansFocusArea(resolvedCompanyId as number, activeFocusAreaId, {
            page: filters.page,
            page_size: filters.pageSize,
            community_id: tableFilters.communityIds,
            plan_status: tableFilters.planStatuses,
            search: filters.search,
            ordering,
          }),
    enabled: Boolean(resolvedCompanyId),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    onSuccess: (data) => setItems({ results: data.results, count: data.count }),
  });

  const communityOptions: SelectAdvancedFilterOptionType[] = (communities ?? []).map((community) => ({
    value: community.id,
    label: community.comunity_name,
  }));

  const rows = items ?? [];

  return (
    <Container>
      <ActionPlansCompanyHeader company={company} />
      <PageContainer>
        <PillsSection data-test-id="action-plans-company-tabs">
          <ActionPlansTabs
            items={tabs}
            activeKey={activeTab}
            onChange={handleTabChange}
            getItemTestId={(key) => `action-plans-company-tab-${key}`}
          />
        </PillsSection>
        <SummarySection>
          <ActionPlansCompanySummary
            stats={stats}
            isOverall={isOverall}
            communityCount={communityOptions.length}
            focusAreaLabel={activeFocusAreaLabel}
            overdueActive={overdueActive}
            onToggleOverdue={handleToggleOverdue}
          />
        </SummarySection>
        <FiltersSection>
          <ActionPlansCompanyFilters
            filters={tableFilters}
            onFiltersChange={handleFiltersChange}
            focusAreaMode={!isOverall}
            communityOptions={communityOptions}
          />
        </FiltersSection>
        {isOverall ? (
          <ActionPlansCompanyOverallTable
            rows={rows}
            page={filters.page}
            pageSize={filters.pageSize}
            total={totalItemsCount}
            onPageChange={setPage}
            ordering={ordering}
            onOrderingChange={handleOrderingChange}
          />
        ) : (
          <ActionPlansCompanyFocusAreaTable
            rows={rows}
            page={filters.page}
            pageSize={filters.pageSize}
            total={totalItemsCount}
            onPageChange={setPage}
            ordering={ordering}
            onOrderingChange={handleOrderingChange}
          />
        )}
      </PageContainer>
    </Container>
  );
};

export default ActionPlansCompanyView;
